// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { handleNotificationError, mergeTag, openMessage, trimTags, useLocalMessage } from '@Helpers/';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import client, { queryClient } from '@Client';
import { useStreamObserver } from '@Hooks/useStream';
import { useGenericQuery } from '@Queries/genericQueries';
import { STREAM_EVENTS } from '@Services/StreamSrv';
import { computeEventList } from '@Organisms/Observable/eventList';
import { THLayout } from '@Molecules/THLayout';
import Details from '@Molecules/Details/Details';
import { extractData } from '@Organisms/Observable/extractData';
import { useCurrentUser } from '@Queries/userQueries';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { Form } from 'antd';
import useListQuery from '@Hooks/useListQuery';
import useOneQuery from '@Hooks/useOneQuery';
import { computeGetSelector } from '@Organisms/Observable/selector';
import { formatObservableTitle } from '@Helpers/observable';
import { actionList } from '@Organisms/Observable/actionList';
import { EntityHeader } from '@Atoms/EntityHeader/EntityHeader';
import { StatusContext } from '@Context/StatusProvider';
import { useDrawer } from '@Hooks/useDrawer';
import {
    AnalyzerOutput,
    ActionOutput,
    AuditOutput,
    ObjectTypeV1,
    ObservableOutput,
    ObservableTypeOutput,
    PermissionsEnum,
    AlertOutput,
} from 'thehive-sdk';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import usePageTitle from '@Hooks/usePageTitle';
import { computeGetSelector as computeGetSelectorAlert } from '@Organisms/Alert/selector';

import './ObservableDetails.scss';

const actionIsObs = (audit: AuditOutput): boolean => audit.base?.details?.objectType === 'Observable';

const AlertObservablesDetails = (): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();

    const [form] = Form.useForm();
    const { alertId, observableId, reportId } = useParams<{
        alertId: string;
        observableId: string;
        reportId: string;
    }>();
    const { hasCortex, config: statusConfig } = useContext(StatusContext);

    const {
        data: observableData,
        error,
        refetch,
    } = useOneQuery<ObservableOutput>({
        controller: client.ObservableController,
        selector: computeGetSelector({ observableId, extraData: [] }),
    });

    const { data: currentUser } = useCurrentUser();
    const { organisation = '', organisations = [], permissions = [] } = currentUser || {};

    const links = useMemo(
        () => organisations.find(({ organisation: orgName }) => orgName === organisation)?.links || [],
        [organisation, organisations],
    );

    const [currentData, setCurrentData] = useState<ObservableOutput>();
    const [updatedData, setUpdatedData] = useState<boolean>(false);
    const [forceUpdate, setForceUpdate] = useState<boolean>(false);

    const hasPermissions = useSplitPermissions(observableData?.extraData?.permissions || permissions, [
        PermissionsEnum.MANAGE_OBSERVABLE,
        PermissionsEnum.MANAGE_ANALYSE,
    ]);

    const { visible: visibleDrawer, setVisible: setVisibleDrawer } = useDrawer({});
    const [editMode, setEditMode] = React.useState(false);
    const [tmpRefetch, setTmpRefetch] = React.useState<string[]>([]);

    const { data: observableReportList, refetch: refetchObservableReportList } = useListQuery<ActionOutput[]>({
        queryParams: {
            selector: {
                name: 'get-all-observable-reports',
                head: [
                    {
                        name: 'getObservable',
                        idOrName: observableId,
                    },
                    {
                        name: 'actions',
                    },
                ],
            },
            sort: ['-startDate'],
        },
        controller: client.ObservableController,
    });

    const { data: observableTypeList = [], refetch: refetchObservableTypeList } = useListQuery<ObservableTypeOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listObservableType' }],
                name: 'get-all-observable-types',
            },
            sort: ['+name'],
        },
        controller: client.ObservableTypeController,
        manual: true,
        fetchOnStart: false,
    });

    const { data: analyzerData = [] } = useGenericQuery<AnalyzerOutput[]>({
        fetchCallback: () => client.AnalyzerController.listAnalyzer(),
        key: ['get-all-analyzer'],
    });

    const { data: analysisData = [], refetch: jobRefetch } = useListQuery<ActionOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getObservable', idOrName: observableId }, { name: 'jobs' }],
                name: `get-job-analyzer-${observableId}`,
            },
            sort: ['-startDate'],
        },
        controller: client.ObservableController,
    });

    const { data: similarData = [] } = useListQuery<ObservableOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getObservable', idOrName: observableId }, { name: 'similar' }, { name: 'dedup' }],
                name: `get-similar-observable-${observableId}`,
                extraData: ['links'],
            },
            paginationOptions: {
                itemsPerPage: 100,
                page: 0,
            },
        },
        controller: client.ObservableController,
    });

    const { data: alertData } = useOneQuery<AlertOutput>({
        selector: computeGetSelectorAlert({ alertId }),
        controller: client.AlertController,
    });

    const analyzer = {
        data: analyzerData,
        onFinish: (items: any[]): Promise<void> =>
            client.AnalyzerController.multipleExecuteJob(items)
                .then(() => {
                    openMessage('success', i('analyzer.launch.success'));
                })
                .catch(() => {
                    openMessage('success', i('analyzer.launch.error'));
                }),
    };

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.ObservableController,
                locale: i,
                onError: handleNotificationError,
                onSuccess: (_, message) => {
                    setForceUpdate(true);
                    if (message) {
                        openMessage('success', message);
                    }
                    refetch();
                    queryClient.refetchQueries(['sharings', observableId]);
                },
            }),
        [refetch, i, observableId],
    );

    const { data: shareList = [] } = useGenericQuery({
        fetchCallback: () =>
            eventList.shares.onList(observableId, [
                {
                    field: 'organisationName',
                    type: 'string',
                    values: organisation,
                    operator: 'not',
                    describeAttribute: '' as any,
                },
            ]),
        key: ['sharings', observableId],
    });

    const editObservable = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ startDate, _createdAt, _updatedAt, data: dataSource, attachment, tags, ...values }) => {
            const treatedTag = tags ? mergeTag(currentData?.tags || [], observableData?.tags || [], tags) : [];
            eventList.onEdit(
                {
                    ...values,
                    ...(tags ? { tags: trimTags(treatedTag) } : {}),
                },
                observableId,
            );
        },
        [observableData, observableId, currentData, eventList],
    );

    useStreamObserver(
        { objectType: STREAM_EVENTS.OBSERVABLE },
        (data) => {
            const canRefetch = data.some((action) => action.base.objectId === observableId);

            if (canRefetch) {
                refetch();
            }
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { objectType: STREAM_EVENTS.ANALYZER },
        (data) => {
            const canRefetch = data.some((action) => action.base.rootId === observableId);

            const successFound = data.some(
                (action) => action.base.action === 'update' && action.base.details.status === 'Success',
            );

            if (successFound && canRefetch) {
                if (!visibleDrawer && !editMode) {
                    refetch();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetch']);
                }
            }
            jobRefetch();
        },
        [visibleDrawer, editMode],
    );

    useStreamObserver(
        { objectType: STREAM_EVENTS.ACTION },
        (audits: AuditOutput[]): void => {
            if (audits.some(actionIsObs)) {
                if (!visibleDrawer && !editMode) {
                    refetchObservableReportList();
                } else {
                    setTmpRefetch([...tmpRefetch, 'refetchObservableReportList']);
                }
            }
        },
        [visibleDrawer, editMode],
    );

    useEffect(() => {
        if (error) {
            openMessage('error', i('notification.observable.doesnotExist'));
            history.push(RouteBuilder.compute('alerts.observables', { alertId }));
        }
    }, [alertId, error, history, i]);

    const { dataType = '', data, attachment } = currentData || {};

    const observableTypeParsed = observableTypeList.find(({ name }) => name === dataType);

    const parsedAnalyzerData = useMemo(
        () =>
            analyzerData
                .filter((analyzer) => analyzer.dataTypeList.includes(dataType))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [analyzerData, dataType],
    );

    const observableActionList = useMemo(
        () =>
            observableData &&
            actionList(
                i,
                history,
                ObjectTypeV1.ALERT,
                alertId,
                undefined,
                analyzer,
                hasCortex,
                eventList,
                statusConfig,
                permissions,
            )([observableData], () => refetch(), 'details'),
        [i, refetch, observableData, alertId], //eslint-disable-line
    );

    useEffect(() => {
        form.resetFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentData]);

    useEffect(() => {
        if (!editMode && updatedData) {
            setCurrentData(observableData);
            setUpdatedData(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, updatedData]);

    useEffect(() => {
        if ((!editMode && observableData) || forceUpdate) {
            setCurrentData(observableData);
            setForceUpdate(false);
            form.resetFields();
        } else {
            setUpdatedData(true);
        }
    }, [observableData, editMode, forceUpdate]); // eslint-disable-line

    useEffect(() => {
        if (!visibleDrawer && !editMode && tmpRefetch.length) {
            tmpRefetch.includes('refetch') && refetch();
            tmpRefetch.includes('refetchObservableReportList') && refetchObservableReportList();
            setTmpRefetch([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleDrawer, tmpRefetch, editMode]);

    const forbiddenFields = useMemo(() => {
        const fields = [];

        if (!links.length) {
            fields.push('#computeShares');
        }

        return fields;
    }, [links]);

    usePageTitle(i('page-title.alerts.observables-details', { title: alertData?.title || '' }));

    return (
        <THLayout>
            <THLayout.Content>
                <THForm<ObservableOutput>
                    form={form}
                    editable
                    initialValues={currentData}
                    onFinish={editObservable}
                    submitTouchedValues
                >
                    <THForm.Layout
                        header={
                            <EntityHeader
                                actionList={observableActionList}
                                onBackClick={() =>
                                    history.push(RouteBuilder.compute('alerts.observables', { alertId }))
                                }
                                title={i('observable.details')}
                            />
                        }
                        footer={
                            <THForm.Item noStyle shouldUpdate>
                                {({ isFieldsTouched }) => {
                                    setEditMode(isFieldsTouched());
                                    return (
                                        <FormSubmitButton
                                            warning
                                            onCancel={() => {
                                                form.resetFields();
                                                setEditMode(false);
                                            }}
                                            disabled={false}
                                            footerText={i('general.save')}
                                            hidden={!isFieldsTouched()}
                                        />
                                    );
                                }}
                            </THForm.Item>
                        }
                    >
                        <Details
                            data={{
                                ...observableData,
                                '#computeAnalyzer': {
                                    analyzerData: parsedAnalyzerData,
                                    analysisData,
                                    observable: observableData,
                                },
                                '#computeObservableLinkList': similarData,
                                '#computeResponderReportList': observableReportList,
                                '#computeShares': { shareList, setVisible: setVisibleDrawer },
                                '#computeDataType': {
                                    observableTypeList: observableTypeList.filter(
                                        ({ isAttachment }) => observableTypeParsed?.isAttachment === isAttachment,
                                    ),
                                    refetchObservableTypeList,
                                },
                                '#computeTitle': formatObservableTitle({ data, attachment }),
                            }}
                            extract={extractData({
                                data: observableData || ({} as ObservableOutput),
                                eventList,
                                locale: i,
                                hasPermissions: { ...hasPermissions, share: false },
                                organisation,
                                shareList,
                                sharedOrgList: [],
                                visibleReportId: reportId,
                            })}
                            forbidden={forbiddenFields}
                        />
                    </THForm.Layout>
                </THForm>
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    path: RouteBuilder.compute('alerts.observable'),
    content: <AlertObservablesDetails />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.alert.observable-details' },
};

export const RouteWithReport: RouteType = {
    path: RouteBuilder.compute('alerts.observable-report'),
    content: <AlertObservablesDetails />,
    space: ['user'],
    breadcrumb: { label: 'breadcrumb.alert.observable-details' },
};
